import type { WebinarResults } from '../types/results';

export async function generatePDF(results: WebinarResults): Promise<void> {
  // Show loading overlay
  const loadingDiv = document.createElement('div');
  loadingDiv.id = 'pdf-loading-overlay';
  loadingDiv.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    ">
      <div style="
        background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        padding: 2.5rem 3rem;
        border-radius: 16px;
        text-align: center;
        border: 1px solid rgba(255,255,255,0.1);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
      ">
        <div style="
          width: 48px;
          height: 48px;
          border: 3px solid rgba(37, 99, 235, 0.3);
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: pdfspin 1s linear infinite;
          margin: 0 auto 1.5rem;
        "></div>
        <div style="
          font-size: 1.25rem;
          font-weight: 600;
          color: white;
          margin-bottom: 0.5rem;
          font-family: system-ui, -apple-system, sans-serif;
        ">Generating Your PDF</div>
        <div style="
          font-size: 0.875rem;
          color: rgba(255,255,255,0.6);
          font-family: system-ui, -apple-system, sans-serif;
        ">This may take a few seconds...</div>
      </div>
    </div>
    <style>
      @keyframes pdfspin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;
  document.body.appendChild(loadingDiv);

  try {
    // Lazy-load @react-pdf/renderer and PDF components to keep main bundle small
    const [React, { pdf }, { WebinarDocument }, parsers] = await Promise.all([
      import('react'),
      import('@react-pdf/renderer'),
      import('../pdf/WebinarDocument'),
      import('./contentParsers'),
    ]);

    // Parse all content using existing parsers
    const offerStack = parsers.parseOfferStack(results.offerStack || '');
    const scriptSections = parsers.parseScriptSections(results.webinarScript || '');
    const marketingEmails = parsers.parseEmails(results.marketingEmails || '');
    const followUpEmails = parsers.parseEmails(results.followUpEmails || '');
    const socialPosts = parsers.parseSocialPosts(results.socialPosts || '');
    const landingPage = results.landingPage
      ? parsers.parseLandingPage(results.landingPage)
      : null;

    // Try to extract a webinar title from script sections
    const webinarTitle =
      scriptSections.length > 0 && scriptSections[0].title
        ? scriptSections[0].title
        : undefined;

    // Create the React PDF document element
    const documentElement = React.createElement(WebinarDocument, {
      webinarTitle,
      offerStack,
      scriptSections,
      marketingEmails,
      followUpEmails,
      socialPosts,
      landingPage,
    });

    // Generate the PDF blob
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const blob = await pdf(documentElement as any).toBlob();

    // Create a download link and trigger it
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Webinar_Package_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('PDF generation failed:', error);
    alert('Failed to generate PDF. Please try again.');
  } finally {
    // Remove loading indicator
    const overlay = document.getElementById('pdf-loading-overlay');
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }
}
